import { useMemo, useState } from "react";
import type { SortDirection } from "@/hooks/use-data-grid";

/**
 * Sorts an array that is already in memory.
 *
 * The list screens sort server-side through useDataGrid because they page. The
 * detail screens hold the whole set already, so a round trip would buy nothing
 * -- but their columns still have to sort, and before this they simply did not.
 *
 * `accessor` maps a column key to the value to order on. That indirection is
 * the point: a Period column must sort on period_start (a real date) while
 * DISPLAYING the period name, and ordering on the name is alphabetical --
 * "April 2024" before "December 2023".
 */
export function useClientSort<T>(
  rows: T[],
  accessor: (row: T, key: string) => string | number | null | undefined,
  defaults: { key: string; dir?: SortDirection },
) {
  const [sort, setSort] = useState(defaults.key);
  const [sortDir, setSortDir] = useState<SortDirection>(defaults.dir ?? "desc");

  const toggleSort = (key: string) => {
    if (key === sort) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSort(key);
    // A new column starts descending: every sortable column here is a figure
    // or a date, and "biggest / most recent first" is the useful first look.
    setSortDir("desc");
  };

  const sorted = useMemo(() => {
    const out = [...rows];
    out.sort((a, b) => {
      const av = accessor(a, sort);
      const bv = accessor(b, sort);
      // Missing values sort last in BOTH directions. Flipping them to the top
      // on a descending sort would put "no margin" above the worst real margin
      // and read as the worst account.
      const aMissing = av === null || av === undefined || av === "";
      const bMissing = bv === null || bv === undefined || bv === "";
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;

      let cmp: number;
      if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return out;
  }, [rows, sort, sortDir, accessor]);

  return { rows: sorted, sort, sortDir, toggleSort };
}
